import React, { useEffect, useState } from 'react';
import { notification, Modal, Row, Button } from 'antd';
import { useMutation, useReactiveVar } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import utils from 'utils/utils';
import CloseIcon from 'components/Icons/CloseIcon';
import ConnectedUserIcon from 'components/Icons/ConnectedUserIcon';
import EclipseIcon from 'components/Icons/EclipseIcon';
import IntegrationItem from '../IntegrationItem';
import { SquareLog } from 'components/Icons';
import { CONNECT_SQUARE, DISCONNECT_SQUARE } from 'graphql/mutations/squareMutations';
import PopConfirm from 'components/PopConfirm';
import { userVar } from 'graphql/cache';

const SquareIntegration = ({
  data: isConnected,
  isModalItem,
  currencies,
  proposal,
  saveProposal,
  userRole,
}) => {
  const [confirmModal, setConfirmModal] = useState(false);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const user = useReactiveVar(userVar);
  const [getAuthUrl, { loading: connectSquareLoading }] = useMutation(CONNECT_SQUARE, {
    onCompleted: (data) => {
      if (data.connectSquare) {
        window.location = data.connectSquare;
      }
    },
    onError: (error) => {
      notification.error({
        message: 'Square Connect Failed',
        description: 'Something went wrong',
      });
    },
  });
  const [disconnectSquare, { loading: disconnectSquareLoading }] = useMutation(DISCONNECT_SQUARE, {
    onCompleted: (data) => {
      if (data.disconnectSquare) {
        notification.success({
          message: 'You have successfully disconnected Square',
        });
        userVar({ ...user, square: null });
        setConfirmModal(false);
      } else {
        notification.error({
          message: 'Square disconnect Failed',
          description: 'Something went wrong',
        });
      }
    },
    onError: (error) => {
      notification.error({
        message: 'Square disconnect Failed',
        description: 'Something went wrong',
      });
    },
  });
  const history = useHistory();

  useEffect(() => {
    const squareError = utils.getQueryStringValue('square-error');
    if (squareError === 'true') {
      notification.error({
        message: 'Failed to connect Square',
      });
    } else if (squareError === 'false') {
      notification.success({
        message: 'You have successfully connected Square',
      });
    }
    history.replace(window.location.pathname);
  }, [history]);

  const handleClick = () => {
    if (isConnected) {
      setConfirmModal(true);
    } else {
      getAuthUrl();
    }
  };

  return (
    <>
      <IntegrationItem
        description="Request payment from clients via Stripe right after they signed your proposal"
        integrationLink="https://support.goprospero.com/article/show/177370-how-to-integrate-square-with-prospero-for-seamless-proposal-payments"
        status={isConnected ? 'connected' : connectSquareLoading ? 'connecting' : 'disconnected'}
        logo={<SquareLog style={{ height: 'auto', width: 'auto ' }} />}
        handleClick={connectSquareLoading || disconnectSquareLoading ? () => {} : handleClick}
        isModalItem={isModalItem}
        currencies={currencies}
        proposal={proposal}
        userRole={userRole}
        // updateEnablePaymentStatus={updateEnablePaymentStatus}
        // enablePaymentStatus={enablePaymentStatus}
        integrationData={isConnected}
        saveProposal={saveProposal}
      />
      {showConnectModal && (
        <Modal
          zIndex={1009}
          className={'confirm-modal wix-connect-modal'}
          centered
          visible={showConnectModal}
          onCancel={() => setShowConnectModal(false)}
          closeIcon={<CloseIcon className="close-icon" />}
          closable={true}
          showIcon={true}
          footer={null}>
          <Row className="wix-connect-modal-logo-container">
            <EclipseIcon className="wix-connect-modal-logo-background" />
            <ConnectedUserIcon className="logo" />
          </Row>

          <div className="title">Stripe Is Now Connected!</div>

          <div className="divider" />

          <div className="body">
            Request payment from clients via Stripe right after they signed your proposal
          </div>

          <div className="button-wrapper">
            <Button
              className="button cancel"
              onClick={() => window.open('https://dashboard.stripe.com/test/dashboard', '_self')}>
              Go to Stripe
            </Button>
          </div>
        </Modal>
      )}
      {confirmModal && (
        <PopConfirm
          title={`Are you sure you want to disconnect Square?`}
          visible={confirmModal}
          cancelText="CANCEL"
          confirmText="DISCONNECT"
          onConfirm={disconnectSquare}
          onCancel={() => setConfirmModal(false)}
          closable={!disconnectSquareLoading}
          okButtonProps={{ loading: disconnectSquareLoading }}
        />
      )}
    </>
  );
};

SquareIntegration.defaultProps = {
  data: null,
  isModalItem: false,
  currencies: [],
  proposal: '',
  saveProposal: () => {},
  userRole: '',
  userEmail: '',
  handleRefetchUser: () => {},
};

SquareIntegration.propTypes = {
  data: PropTypes.oneOfType([
    PropTypes.instanceOf(Object),
    PropTypes.instanceOf(Array),
    PropTypes.instanceOf(null),
  ]),
  isModalItem: PropTypes.bool,
  currencies: PropTypes.arrayOf(PropTypes.shape({})),
  proposal: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Object)]),
  saveProposal: PropTypes.func,
  userRole: PropTypes.string,
  userEmail: PropTypes.string,
  handleRefetchUser: PropTypes.func,
};

export default SquareIntegration;
